import JwtDecode from 'jwt-decode';
import { action, computed, makeObservable, observable } from 'mobx';

import AuthState from './Models/AuthState';
import TokenDecoded from './Models/TokenDecoded';
import User from './Models/User';
import Agency from './Models/Agency';
import LoggerFactory from '../../Util/Logger/LoggerFactory';
import { loginEvent, logoutEvent } from '../../Events/commonEvents';
import { ApiRole } from '@ibe/api';

class AuthService {
  private logger = LoggerFactory.get('AuthService');

  jwtDecode = JwtDecode;

  private authState = new AuthState();

  constructor() {
    makeObservable<AuthService, 'authState' | 'abort' | 'updateState'>(this, {
      authState: observable,
      state: computed,
      isLoggedIn: computed,
      fromJwt: action,
      abort: action,
      authenticate: action,
      invalidate: action,
      updateState: action
    });
  }

  get state(): AuthState {
    return this.authState;
  }

  get isLoggedIn(): boolean {
    return this.authState.isLoggedIn;
  }

  public fromJwt(token: string): void {
    try {
      const tokenDecoded = this.decode<TokenDecoded>(token);
      if (tokenDecoded) {
        const user: User = {
          id: tokenDecoded.USER_ID?.toString() || undefined,
          username: tokenDecoded.USER_NAME,
          lastName: tokenDecoded.LASTNAME,
          lastname: tokenDecoded.LASTNAME,
          firstname: tokenDecoded.FIRSTNAME,
          firstName: tokenDecoded.FIRSTNAME
        };

        const agency: Agency = {
          number: tokenDecoded.AGENCY_NUMBER,
          language: tokenDecoded.LANGUAGE,
          currency: tokenDecoded.CURRENCY
        };

        const newState: AuthState = {
          firstName: tokenDecoded.FIRSTNAME,
          lastName: tokenDecoded.LASTNAME,
          user,
          agency,
          role: tokenDecoded.ROLE as ApiRole,
          token,
          isAgency: !!tokenDecoded.AGENCY_NUMBER,
          isLoggedIn: !!tokenDecoded.USER_ID && tokenDecoded.ROLE !== ApiRole.GUEST,
          isGuest: tokenDecoded.ROLE === ApiRole.GUEST
        };
        this.updateState(newState);
      }
    } catch (e) {
      this.logger.warn(e);
      this.abort('Could not create state from token.');
    }
  }

  getRemainingTokenTime(): number | null {
    if (!this.authState || !this.authState.token) {
      return null;
    }

    let expirationTime: number | null = null;
    const { token } = this.state;

    if (token) {
      const tokenDecoded = this.decode<TokenDecoded>(token);
      if (tokenDecoded) {
        expirationTime = tokenDecoded.exp * 1000 - Date.now();
      }
    }

    return expirationTime;
  }

  public getTokenExpiration(): number | null {
    if (!this.authState || !this.authState.token) {
      return null;
    }

    let expirationTime: number | null = null;
    const { token } = this.state;

    if (token) {
      const tokenDecoded = this.decode<TokenDecoded>(token);
      if (tokenDecoded) {
        expirationTime = tokenDecoded.exp * 1000 - tokenDecoded.iat * 1000;
      }
    }

    return expirationTime;
  }

  protected decode<M>(token: string): M {
    return this.jwtDecode<M>(token);
  }

  protected abort(because: string): void {
    this.invalidate();
    throw new Error(because);
  }

  public invalidate(): void {
    this.updateState(new AuthState());
    logoutEvent.broadcast();
  }

  public authenticate(token: string): void {
    this.fromJwt(token);

    if (this.authState.isLoggedIn) {
      loginEvent.broadcast(this.authState);
    }
  }

  protected updateState(state: AuthState): void {
    this.authState = state;
  }
}

export default AuthService;
